/**
 * THE LOCATION'S TIP CHOICES, SURCHARGE RATE & CARD MINIMUM — editable in
 * Super Admin with the merchant's rules (platform-app TerminalPaymentTypes):
 *
 *   tip choices   tenantTipConfig.{percentages, allowCustom, allowSkip}
 *   surcharge     tenantSurchargeConfig.{rateOverride, flatOverride}  (null = use the max)
 *   minimum       minCardAmount
 *
 * Stored on the location's gateway row; written through the merchant's own
 * route (ext2 PUT /pos/locations/{loc}/nmi-cloud-config), changed keys only.
 * `enabled` / `cashEnabled` (the merchant's own tip switches) are never sent.
 *
 * DEFAULTS: a location with no percentages shows the customer display's own
 * defaults (15 · 18 · 20 %, Custom, No tip — AGENTIC_POS CustomerDisplay) as a
 * SUGGESTION, marked "Default — not saved". Nothing is written until an admin
 * accepts or edits them and presses Save: today the till asks for no tip at a
 * location without percentages, and that never changes by itself.
 */

/** Same values and words as the customer display tip screen. */
export const DEFAULT_TIP_PERCENTAGES = [15, 18, 20];
export const MAX_TIP_CHOICES = 10;

export interface LocationTip { percentages: number[]; allowCustom: boolean; allowSkip: boolean }
export interface LocationValues {
  /** null = never set at this location (the suggestion is shown). */
  tip: LocationTip | null;
  /** The location's rate / flat. null = none set — the till uses the max. */
  rate: number | null;
  flat: number | null;
  min: number;
}
export interface LocationDraft {
  tip: LocationTip;
  /** The suggestion was accepted or edited — the tip block counts as a change. */
  tipAccepted: boolean;
  rateStr: string;
  flatStr: string;
  minStr: string;
}

const num = (v: unknown, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || d);

/** Explicit rows are literal; legacy 0/absent = "use the max" (mirrors platform-app readOverride). */
export function readOverride(cfg: any, key: 'rateOverride' | 'flatOverride'): number | null {
  const v = cfg?.[key];
  if (cfg?.overridesExplicit === true) return typeof v === 'number' && Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function locationValuesFrom(gw: any): LocationValues {
  const t = gw?.tenantTipConfig;
  const pcts = Array.isArray(t?.percentages) ? t.percentages.filter((n: unknown) => typeof n === 'number').sort((a: number, b: number) => a - b) : [];
  return {
    tip: pcts.length ? { percentages: pcts, allowCustom: t?.allowCustom !== false, allowSkip: t?.allowSkip !== false } : null,
    rate: readOverride(gw?.tenantSurchargeConfig, 'rateOverride'),
    flat: readOverride(gw?.tenantSurchargeConfig, 'flatOverride'),
    min: num(gw?.minCardAmount),
  };
}

export const fmtOverride = (v: number | null) => (v === null ? '' : v.toFixed(2));

export function draftFrom(v: LocationValues): LocationDraft {
  return {
    tip: v.tip ? { ...v.tip, percentages: [...v.tip.percentages] } : { percentages: [...DEFAULT_TIP_PERCENTAGES], allowCustom: true, allowSkip: true },
    tipAccepted: false,
    rateStr: fmtOverride(v.rate), flatStr: fmtOverride(v.flat), minStr: v.min.toFixed(2),
  };
}

export const parseDec = (s: string): number | null => {
  const t = s.trim().replace(/^\$/, '');
  if (t === '') return 0;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
};
/** Empty = "use the max" (null); `undefined` = not a number. */
export const parseOverride = (s: string): number | null | undefined => {
  if (s.trim().replace(/^\$/, '') === '') return null;
  const n = parseDec(s);
  return n === null ? undefined : n;
};

const sameList = (a: number[], b: number[]) => a.length === b.length && a.every((x, i) => x === b[i]);

export interface LocationDiff { tip: boolean; rate: boolean; flat: boolean; min: boolean }
export function locationDiff(base: LocationValues, d: LocationDraft): LocationDiff {
  const tip = base.tip
    ? !sameList(base.tip.percentages, d.tip.percentages) || base.tip.allowCustom !== d.tip.allowCustom || base.tip.allowSkip !== d.tip.allowSkip
    : d.tipAccepted;
  const r = parseOverride(d.rateStr); const f = parseOverride(d.flatStr); const m = parseDec(d.minStr);
  return { tip, rate: r !== base.rate, flat: f !== base.flat, min: m !== null && m !== base.min };
}
export const locationChangeCount = (diff: LocationDiff) =>
  (diff.tip ? 1 : 0) + (diff.rate || diff.flat ? 1 : 0) + (diff.min ? 1 : 0);

/** Why the draft cannot be saved, or null. */
export function locationProblem(d: LocationDraft): string | null {
  const r = parseOverride(d.rateStr); const f = parseOverride(d.flatStr);
  if (r === undefined || f === undefined || parseDec(d.minStr) === null) return 'Enter amounts as numbers, e.g. 3.00';
  /* Same rule as the merchant screen (platform-app TODO(surcharge-zero)): tills
     on the current release read a stored 0 as "use the max". */
  if (r === 0 || f === 0) return '0 isn’t available yet — leave the box empty to use the maximum.';
  return null;
}

/** The PUT body — changed keys only; never the merchant's own tip switches. */
export function buildLocationBody(base: LocationValues, d: LocationDraft): Record<string, unknown> | null {
  const diff = locationDiff(base, d);
  const body: Record<string, unknown> = {};
  if (diff.tip) body.tenantTipConfig = { percentages: d.tip.percentages, allowCustom: d.tip.allowCustom, allowSkip: d.tip.allowSkip };
  if (diff.rate || diff.flat) {
    const sc: Record<string, number | null> = {};
    if (diff.rate) sc.rateOverride = parseOverride(d.rateStr) as number | null;
    if (diff.flat) sc.flatOverride = parseOverride(d.flatStr) as number | null;
    body.tenantSurchargeConfig = sc;
  }
  if (diff.min) body.minCardAmount = parseDec(d.minStr) as number;
  return Object.keys(body).length ? body : null;
}

/** "Good / Great / Best" — the customer display's words (AGENTIC_POS TipScreen.tipWords). */
export function tipWord(pcts: number[], p: number): 'Good' | 'Great' | 'Best' {
  const sorted = [...new Set(pcts)].sort((a, b) => a - b);
  const i = sorted.indexOf(p);
  if (i === sorted.length - 1) return 'Best';
  if (sorted.length === 2) return 'Great';
  return i < Math.floor((sorted.length - 1) / 2) ? 'Good' : 'Great';
}
